import { FC, useEffect, useRef, useState } from "react";
import imageEmail from "../../../assets/images/emailImage.png";
import "./validate.css";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import api from "../../../services/api";
import { usePluginspace } from "../../../hooks/usePluginspace";
import { cpfCnpjNoMask } from "../../../functions/mask";
import { Loading } from "../../../components/Loading";
import useAuth from "../../../hooks/useAuth";
import { parseJwt } from "../../../functions/util";
import { DecodedTokenData } from "../../../contexts/authContext";
import { useAppConfig } from "../../../hooks/useAppConfig"; // Para as cores e config

const JWT_SALT = process.env.REACT_APP_JWT_SALT || "ninegridCrypoToken";

const ValidateAccount: FC = () => {
  const [code, setCode] = useState(["", "", "", ""]);
  const inputRefs = useRef<any>([]);
  const navigate = useNavigate();
  const { pluginspace_id } = useParams();
  const { getToken } = usePluginspace();
  const [loading, setLoading] = useState(false);
  const { setPluginspaceData } = usePluginspace();
  const { setLoginInfo } = useAuth();
  const { getAppConfig } = useAppConfig();

  const [primaryColor, setPrimaryColor] = useState("#078BD8");

  // Busca as configurações visuais na montagem
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const configData = await getAppConfig();
        if (configData?.webappcolor?.webappbutton) {
          setPrimaryColor(configData.webappcolor.webappbutton);
        }
      } catch (error) {
        console.log(error);
      }
    };
    fetchConfig();
  }, []);

  const handleChange = (e: any, index: number) => {
    const { value } = e.target;
    // Permite apenas números (um dígito)
    if (/^\d$/.test(value)) {
      const newCode = [...code];
      newCode[index] = value;
      setCode(newCode);
      if (index < 3) {
        inputRefs.current[index + 1].focus();
      }
    } else if (value === "" && index > -1) {
      // Se apagou, volta o foco
      const newCode = [...code];
      newCode[index] = value;
      setCode(newCode);
      inputRefs.current[index === 0 ? index : index - 1].focus();
    }
  };

  const validar = async () => {
    setLoading(true);
    const ppLS = JSON.parse(localStorage.getItem("payload")!);
    const payload = {
      email: ppLS.email,
      doc: cpfCnpjNoMask(ppLS.doc),
      validationCode: code.join(""),
    };

    await api
      .post(`/api/customer/validCodeCustomer`, payload, {
        headers: {
          Authorization: `Bearer ${await getToken()}`,
        },
      })
      .then(async (response: any) => {
        setLoading(false);
        const data = response.data;
        if (!data || !data.success) {
          Swal.fire({
            icon: "error",
            title: "Erro",
            text: "Erro na validação do código, tente novamente!",
          });
          return;
        }

        if (data.pemission?.accessToken) {
          const token = data.pemission.accessToken;
          localStorage.setItem("loginInfo", JSON.stringify(token));
          const tkNoSalt = token.split(JWT_SALT).join("");
          const tk: DecodedTokenData | null = parseJwt(tkNoSalt);

          if (tk) {
            setLoginInfo({
              accessToken: token,
              expiratedAt: data.pemission.expiratedAt,
              id: tk.id,
              identifier: tk.identifier,
              email: tk.email,
              plugintype: tk.plugintype,
              pluginspaceId: tk.pluginspaceId,
              plugintypeId: tk.plugintypeId,
              profile: tk.profile,
            });

            setPluginspaceData(tk?.pluginspaceId);
            navigate(`/${pluginspace_id}/register/success`);
          }
        } else {
          Swal.fire({
            icon: "error",
            title: "Erro",
            text: "Erro na validação do código, tente novamente!",
          });
        }
      })
      .catch((err: any) => {
        setLoading(false);
        Swal.fire({
          icon: "error",
          title: "Erro",
          text: "Erro na validação do código, tente novamente!",
        });
        console.log(err);
      });
  };

  async function resendEmail() {
    setLoading(true);
    const ppLS = JSON.parse(localStorage.getItem("payload")!);
    const payload = {
      email: ppLS.email,
      doc: cpfCnpjNoMask(ppLS.doc),
    };

    await api
      .post(`/api/customer/resend/validCodeCustomer`, payload, {
        headers: {
          Authorization: `Bearer ${await getToken()}`,
        },
      })
      .then(async (response: any) => {
        setLoading(false);
        const data = response.data;

        if (!data || !data.success) {
          Swal.fire({
            icon: "error",
            title: "Erro",
            text: "Erro no reenvio do código!",
          });
          return;
        } else {
          Swal.fire({
            icon: "success",
            title: "Sucesso",
            text: "Código reenviado com sucesso!",
          });
        }
      })
      .catch((err: any) => {
        setLoading(false);
        Swal.fire({
          icon: "error",
          title: "Erro",
          text: "Erro no reenvio do código!",
        });
        console.log(err);
      });
  }

  return (
    <>
      {loading && (
        <div className="loading-container">
          <Loading type="spinningBubbles" color="#FFF" width={64} height={64} />
        </div>
      )}

      <div className="content-main">
        {/* Botão de Voltar Padronizado */}
        <button className="btn-back" onClick={() => navigate(-1)}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
          Voltar
        </button>

        <div className="validate-image">
          <img src={imageEmail} alt="Validação de E-mail" />
        </div>

        <div className="validate-header">
          <h1>Validação de e-mail</h1>
          <span>
            Enviamos uma mensagem para o seu email com um código de 4 dígitos.
          </span>
        </div>

        <div className="code-container">
          {code.map((digit, index) => (
            <input
              key={index} // Não usar template string no key para o input não desmontar no React
              type="text"
              maxLength={1} // Limita fisicamente a 1 char
              className="digit-input"
              value={digit}
              onChange={(e) => handleChange(e, index)}
              ref={(el) => (inputRefs.current[index] = el)}
            />
          ))}
        </div>

        <div className="msg-send-again">
          <span>Não recebeu a mensagem?</span>
          <a style={{ color: primaryColor }} onClick={() => resendEmail()}>
            Clique aqui para enviar novamente
          </a>
        </div>

        <button
          className="btn-primary"
          style={{ background: primaryColor }}
          onClick={() => validar()}
        >
          Validar
        </button>
      </div>
    </>
  );
};

export { ValidateAccount };
